import * as React from "react";
import { FastField, Form } from "formik";
import { ColorPicker, FormGroup, Input, Textarea } from "@lib/components";
import { Button } from "@lib/components";
import { RotateLoader } from "@lib/components";
import { MobxComponent } from "../../../../../../../mobx/components/index";
import { inject, observer } from "mobx-react";
import { RestaurantForm } from "../../../../../../../mobx/components/restaurant-form";
import { UI } from "../../../../../../../core/ui";
import { HTMLEditor } from "../../../../../../components/html-editor";

interface Props {}
interface State {}
interface FormValues {
	cookie: T.Schema.Restaurant.RestaurantSchema["settings"]["cookie"];
}

// https://termly.io/resources/templates/cookie-policy-template/
const cookieTemplateText = `<p><strong class="ql-size-large">Cookie Policy of&nbsp;&lt;&lt;RESTAURANT NAME&gt;&gt;</strong></p><hr class="ql-hr" style="height:0;margin-top:10px;margin-bottom:10px"><p><strong>&lt;&lt;RESTAURANT NAME&gt;&gt;</strong> (&ldquo;we&rdquo; or &ldquo;us&rdquo; or &ldquo;our&rdquo;) may use cookies, web beacons, tracking pixels, and other tracking technologies when you visit our website [<strong>&lt;&lt;WEBSITE URL&gt;&gt;</strong>], including any other media form, media channel, mobile website, or mobile application related or connected thereto (collectively, the &ldquo;Site&rdquo;) to help customize the Site and improve your experience.</p><p><br></p><p>We reserve the right to make changes to this Cookie Policy at any time and for any reason. We will alert you about any changes by updating the &ldquo;Last Updated&rdquo; date of this Cookie Policy. Any changes or modifications will be effective immediately upon posting the updated Cookie Policy on the Site, and you waive the right to receive specific notice of each such change or modification.</p><p><br></p><p>You are encouraged to periodically review this Cookie Policy to stay informed of updates. You will be deemed to have been made aware of, will be subject to, and will be deemed to have accepted the changes in any revised Cookie Policy by your continued use of the Site after the date such revised Cookie Policy is posted.</p><p><br></p><p><strong class="ql-size-large">USE OF COOKIES</strong></p><hr class="ql-hr" style="height:0;margin-top:10px;margin-bottom:10px"><p>A &ldquo;cookie&rdquo; is a string of information which assigns you a unique identifier that we store on your computer. Your browser then provides that unique identifier to use each time you submit a query to the Site. We use cookies on the Site to, among other things, keep track of services you have used, record registration information, record your user preferences, keep you logged into the Site, facilitate purchase procedures, and track the pages you visit. Cookies help us understand how the Site is being used and improve your user experience.</p><p><br></p><p><strong class="ql-size-large">TYPES OF COOKIES</strong></p><hr class="ql-hr" style="height:0;margin-top:10px;margin-bottom:10px"><p>The following types of cookies may be used when you visit the Site:</p><p><br></p><p><strong>Advertising Cookies</strong></p><p>Advertising cookies are placed on your computer by advertisers and ad servers in order to display advertisements that are most likely to be of interest to you. These cookies allow advertisers and ad servers to gather information about your visits to the Site and other websites, alternate the ads sent to a specific computer, and track how often an ad has been viewed and by whom. These cookies are linked to a computer and do not gather any personal information about you.</p><p><br></p><p><strong>Analytics Cookies</strong></p><p>Analytics cookies monitor how users reached the Site, and how they interact with and move around once on the Site. These cookies let us know what features on the Site are working the best and what features on the Site can be improved.</p><p><br></p><p><strong>Our Cookies</strong></p><p>Our cookies are &ldquo;first-party cookies&rdquo;, and can be either permanent or temporary. These are necessary cookies, without which the Site won't work properly or be able to provide certain features and functionalities. Some of these may be manually disabled in your browser, but may affect the functionality of the Site.</p><p><br></p><p><strong>Personalization Cookies</strong></p><p>Personalization cookies are used to recognize repeat visitors to the Site. We use these cookies to record your browsing history, the pages you have visited, and your settings and preferences each time you visit the Site.</p><p><br></p><p><strong>Security Cookies</strong></p><p>Security cookies help identify and prevent security risks. We use these cookies to authenticate users and protect user data from unauthorized parties.</p><p><br></p><p><strong>Site Management Cookies</strong></p><p>Site management cookies are used to maintain your identity or session on the Site so that you are not logged off unexpectedly, and any information you enter is retained from page to page. These cookies cannot be turned off individually, but you can disable all cookies in your browser.</p><p><br></p><p><strong>Third-Party Cookies</strong></p><p>Third-party cookies may be place on your computer when you visit the Site by companies that run certain services we offer. These cookies allow the third parties to gather and track certain information about you. These cookies can be manually disabled in your browser.</p><p><br></p><p><strong class="ql-size-large">CONTROL OF COOKIES</strong></p><hr class="ql-hr" style="height:0;margin-top:10px;margin-bottom:10px"><p>Most browsers are set to accept cookies by default. However, you can remove or reject cookies in your browser&rsquo;s settings. Please be aware that such action could affect the availability and functionality of the Site.</p><p><br></p><p>For more information on how to control cookies, check your browser or device&rsquo;s settings for how you can control or reject cookies.</p><p><br>In addition, you may opt-out of some third-party cookies through the Network Advertising Initiative&rsquo;s Opt-Out Tool.</p><p><br></p><p><strong class="ql-size-large">OTHER TRACKING TECHNOLOGIES</strong></p><hr class="ql-hr" style="height:0;margin-top:10px;margin-bottom:10px"><p>In addition to cookies, we may use web beacons, pixel tags, and other tracking technologies on the Site to help customize the Site and improve your experience. A &ldquo;web beacon&rdquo; or &ldquo;pixel tag&rdquo; is tiny object or image embedded in a web page or email. They are used to track the number of users who have visited particular pages and viewed emails, and acquire other statistical data. They collect only a limited set of data, such as a cookie number, time and date of page or email view, and a description of the page or email on which they reside. Web beacons and pixel tags cannot be declined. However, you can limit their use by controlling the cookies that interact with them.</p><p><br></p><p><strong class="ql-size-large">PRIVACY POLICY</strong></p><hr class="ql-hr" style="height:0;margin-top:10px;margin-bottom:10px"><p>For more information about how we use information collected by cookies and other tracking technologies, please refer to our Privacy Policy on the Site. This Cookie Policy is part of and is incorporated into our Privacy Policy. By using the Site, you agree to be bound by this Cookie Policy and our Privacy Policy.</p><p><br></p><p><strong class="ql-size-large">CONTACT US</strong></p><hr class="ql-hr" style="height:0;margin-top:10px;margin-bottom:10px"><p>If you have questions or comments about this Cookie Policy, do not hesitate to contact us.</p><p><br></p>`;

const defaultsCookieConsentBannerText =
	"We use cookies to improve your experience on our site. By clicking 'Accept' or continuing to browse, you are giving your consent for us to set cookies.";

const col_props = {
	className: "col",
	small_title: true,
	no_border: true,
};

@inject("store")
@observer
export class SettingsFormCookie extends MobxComponent<Props, State> {
	initialValues: FormValues;
	constructor(props: Props) {
		super(props);
		this.state = {};
		const r = this.injected.store.restaurant!;
		this.initialValues = {
			cookie: r.settings.cookie || {
				content: "",
				banner_color: "#2B373B",
				text_color: "#ffffff",
				banner_text: defaultsCookieConsentBannerText,
				link_text: "Learn more",
				accept_button_text: "Accept",
				reject_button_text: "Reject",
			},
		};
	}

	render() {
		return (
			<RestaurantForm<FormValues>
				submit={async (r, values) => {
					r.settings.cookie = values.cookie;
					const update = {
						$set: { "settings.cookie": values.cookie },
					};
					return { r, update };
				}}
				initialValues={this.initialValues}
				onError={() => UI.notification.error("An error occurred")}
				onSuccessMessage="Settings Updated"
			>
				{({ form, error }) => {
					const { isSubmitting, setFieldValue } = form;
					return (
						<div className="p-4">
							<FormGroup>
								<p className="lhp">
									Entering Cookie Policy will add the link at
									the bottom of the front store for end-users
									to access it any time.
								</p>
							</FormGroup>

							<FormGroup>
								<FastField
									name="cookie.content"
									render={({ field }: any) => (
										<HTMLEditor
											id="cookie-policy-content"
											initialValue={field.value}
											templatePrompt={
												"This Cookie Policy template is NOT authorized or written by a lawyer.\n\nIt is simply there to help you get started. It may not be enforceable depending on your countries laws.\n\nProceed?"
											}
											templateText={cookieTemplateText}
											onChange={(val) =>
												setFieldValue(
													"cookie.content",
													val
												)
											}
										/>
									)}
								/>
							</FormGroup>

							<FormGroup
								title="Cookie Consent Banner Colors"
								contentClassName="grid-2 sm sm-gap max300"
							>
								<FastField
									name="cookie.banner_color"
									render={({ field }: any) => (
										<FormGroup
											title="Background"
											{...col_props}
										>
											<ColorPicker
												color={field.value}
												onChange={(color) =>
													setFieldValue(
														"cookie.banner_color",
														color
													)
												}
											/>
										</FormGroup>
									)}
								/>
								<FastField
									name="cookie.text_color"
									render={({ field }: any) => (
										<FormGroup title="Text" {...col_props}>
											<ColorPicker
												color={field.value}
												onChange={(color) =>
													setFieldValue(
														"cookie.text_color",
														color
													)
												}
											/>
										</FormGroup>
									)}
								/>
							</FormGroup>

							<FormGroup
								title="Cookie Consent Banner Text"
								help="The text to display in your Cookie Consent Banner"
							>
								<FastField
									name="cookie.banner_text"
									render={({ field }: any) => (
										<Textarea
											{...field}
											value={field.value}
										/>
									)}
								/>
							</FormGroup>

							<FormGroup
								title="Cookie Consent Banner Cookie Policy Link Text"
								help="The Link Text that allow end users to visit your Cookie Policy Page. It will be displayed at the end of your Cookie Consent Banner Text."
							>
								<FastField
									name="cookie.link_text"
									render={({ field }: any) => (
										<Input {...field} value={field.value} />
									)}
								/>
							</FormGroup>

							<FormGroup
								title="Cookie Consent Banner Accept Button Text"
								help="The text to display in your Cookie Consent Banner Button."
							>
								<FastField
									name="cookie.accept_button_text"
									render={({ field }: any) => (
										<Input {...field} value={field.value} />
									)}
								/>
							</FormGroup>

							<FormGroup
								title="Cookie Consent Banner Reject Button Text"
								help="The text to display in your Cookie Consent Banner Reject Button. Clicking Reject will close the banner, but it will show again on the next visit."
							>
								<FastField
									name="cookie.reject_button_text"
									render={({ field }: any) => (
										<Input {...field} value={field.value || ""} />
									)}
								/>
							</FormGroup>

							{error && <FormGroup error={error} />}

							<Button
								full={true}
								color="primary"
								type="submit"
								disabled={isSubmitting}
							>
								{isSubmitting && (
									<RotateLoader size={2} color="white" />
								)}
								{!isSubmitting && "Save"}
							</Button>
						</div>
					);
				}}
			</RestaurantForm>
		);
	}
}
